import { useState, useEffect, useCallback } from 'react'
import { eventBusService } from '../services/eventBusService'

type UseEventBusOptions = {
    initialResources?: { answer_ids?: string[]; quest_ids?: string[] }
}

export function subscribeToEventBusConnectionState(
    setIsConnected: (isConnected: boolean) => void,
    initialResources?: UseEventBusOptions['initialResources'],
): () => void {
    eventBusService.connect(initialResources)

    const updateConnectionState = () => {
        const connected = eventBusService.isConnected()
        setIsConnected(connected)
    }

    const unsubscribeFromConnected = eventBusService.onConnected(updateConnectionState)
    updateConnectionState()

    return unsubscribeFromConnected
}

export function useEventBus(options?: UseEventBusOptions) {
    const [isConnected, setIsConnected] = useState(false)

    useEffect(() => {
        const unsubscribeFromConnected = subscribeToEventBusConnectionState(
            setIsConnected,
            options?.initialResources,
        )

        return () => {
            // The service is a singleton; only release this hook's listener.
            unsubscribeFromConnected()
        }
    }, [])

    const follow = useCallback((resourceType: 'answer' | 'quest' | 'user', resourceId: string) => {
        eventBusService.follow(resourceType, resourceId)
    }, [])

    const unfollow = useCallback(
        (resourceType: 'answer' | 'quest' | 'user', resourceId: string) => {
            eventBusService.unfollow(resourceType, resourceId)
        },
        [],
    )

    return {
        eventBus$: eventBusService.getEventBus$(),
        getTopic$: eventBusService.getTopic$.bind(eventBusService),
        getResourceTopic$: eventBusService.getResourceTopic$.bind(eventBusService),
        follow,
        unfollow,
        isSubscribedTo: eventBusService.isSubscribedTo.bind(eventBusService),
        isConnected,
    }
}
